import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { getFullPhoneE164 } from '@/lib/utils';
import { useHiddenConversations } from './useHiddenConversations';

// ─── Types ──────────────────────────────────────────────────────────

export interface ConversationItem {
  id: string;
  entityType: 'contact' | 'lead';
  full_name: string;
  phone: string;
  avatar_url?: string;
  lastMessage: string | null;
  lastMessageTime: string | null;
  lastMessageDirection: string | null;
  unreadCount: number;
  hasMySentMessages: boolean;
  isAssignedToMe: boolean;
}

export type InboxFilter = 'all' | 'mine' | 'unanswered';

// ─── Template preview helper ────────────────────────────────────────

function useTemplateResolver() {
  const [templates, setTemplates] = useState<Array<{ template_id: string; body_text: string; name: string }>>([]);

  useEffect(() => {
    supabase
      .from('whatsapp_message_templates')
      .select('template_id, body_text, name')
      .then(({ data }) => setTemplates(data || []));
  }, []);

  const resolveBody = useCallback((body: string | null): string => {
    if (!body) return '';
    const match = body.match(/\[Template:\s*([^\]]+)\]/);
    if (match) {
      const tpl = templates.find(t => t.template_id === match[1]);
      if (tpl && tpl.body_text?.trim()) return tpl.body_text;
    }
    return body;
  }, [templates]);

  return resolveBody;
}

// ─── Hook ───────────────────────────────────────────────────────────

export function useInboxConversations() {
  const { data: currentAppUser } = useCurrentAppUser();
  const currentAppUserId = currentAppUser?.id || null;

  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<InboxFilter>('mine');
  const [unknownCount, setUnknownCount] = useState(0);

  const resolveBody = useTemplateResolver();
  const { hide, unhide, isHidden } = useHiddenConversations();

  // ─── Load conversations ─────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!currentAppUserId) return;

    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          contact_id,
          lead_id,
          body,
          direction,
          sent_at,
          received_at,
          read_at,
          sent_by_app_user_id,
          num_media,
          media_type,
          media_storage_path,
          media_filename,
          contacts (
            id,
            first_name,
            last_name,
            primary_phone,
            primary_phone_country_code
          ),
          leads (
            id,
            first_name,
            last_name,
            primary_phone,
            contact_id
          )
        `)
        .eq('channel', 'whatsapp')
        .in('source', ['twilio_divus', 'live'])
        .or('to_e164.eq.+16893145090,from_e164.eq.+16893145090')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (error) throw error;

      const conversationsMap = new Map<string, ConversationItem>();
      const unreadCountMap = new Map<string, number>();
      const mySentMessagesMap = new Map<string, boolean>();

      data?.forEach(item => {
        let entityId = item.contact_id || item.lead_id;
        const entityType: 'contact' | 'lead' = item.contact_id ? 'contact' : 'lead';
        const entity = item.contacts || item.leads;

        // Unify leads converted to contacts
        if (item.lead_id && !item.contact_id && (item.leads as any)?.contact_id) {
          entityId = (item.leads as any).contact_id;
        }

        const key = `${entityType}-${entityId}`;

        if (item.direction === 'inbound' && !item.read_at) {
          unreadCountMap.set(key, (unreadCountMap.get(key) || 0) + 1);
        }

        if (item.direction === 'outbound' && item.sent_by_app_user_id === currentAppUserId) {
          mySentMessagesMap.set(key, true);
        }

        if (entity && !conversationsMap.has(key)) {
          const messageTime = item.sent_at || item.received_at;
          // isAssignedToMe removido: contato deixou de ter dono.
          // "Minhas conversas" passa a depender apenas de hasMySentMessages.
          conversationsMap.set(key, {
            id: entityId!,
            entityType,
            full_name: `${(entity as any).first_name || ''} ${(entity as any).last_name || ''}`.trim() || 'Sem nome',
            phone: entityType === 'contact'
              ? getFullPhoneE164((entity as any).primary_phone, (entity as any).primary_phone_country_code)
              : (entity as any).primary_phone || '',
            lastMessage: item.body,
            lastMessageTime: messageTime,
            lastMessageDirection: item.direction,
            unreadCount: 0,
            hasMySentMessages: false,
            isAssignedToMe: false,
          });
        }
      });

      conversationsMap.forEach((conv, key) => {
        conv.unreadCount = unreadCountMap.get(key) || 0;
        conv.hasMySentMessages = mySentMessagesMap.get(key) || false;
      });

      const sorted = Array.from(conversationsMap.values()).sort(
        (a, b) => new Date(b.lastMessageTime || 0).getTime() - new Date(a.lastMessageTime || 0).getTime()
      );

      setConversations(sorted);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentAppUserId]);

  // ─── Initial load + realtime ──────────────────────────────────
  useEffect(() => {
    if (!currentAppUserId) return;
    loadConversations();

    const channel = supabase
      .channel('inbox-conversations-rt')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: 'channel=eq.whatsapp',
      }, () => loadConversations())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentAppUserId, loadConversations]);

  // ─── Unknown count ────────────────────────────────────────────
  useEffect(() => {
    const loadUnknownCount = async () => {
      const { data: orphanMessages } = await supabase
        .from('messages')
        .select('id, from_e164, to_e164')
        .eq('direction', 'inbound')
        .eq('channel', 'whatsapp')
        .is('contact_id', null)
        .is('lead_id', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!orphanMessages || orphanMessages.length === 0) {
        setUnknownCount(0);
        return;
      }

      const uniquePhones = new Set(orphanMessages.map(m => m.from_e164 || m.to_e164));
      setUnknownCount(uniquePhones.size);
    };

    loadUnknownCount();

    const channel = supabase
      .channel('unknown-messages-count-v2')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: 'channel=eq.whatsapp',
      }, () => loadUnknownCount())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // ─── Mark as unread ───────────────────────────────────────────
  const markAsUnread = useCallback(async (entityId: string, entityType: 'contact' | 'lead') => {
    try {
      let query = supabase
        .from('messages')
        .select('id')
        .eq('channel', 'whatsapp')
        .eq('direction', 'inbound')
        .order('created_at', { ascending: false })
        .limit(1);

      if (entityType === 'contact') {
        query = query.eq('contact_id', entityId);
      } else {
        query = query.eq('lead_id', entityId);
      }

      const { data: msgs, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      if (msgs && msgs.length > 0) {
        const { error: updateError } = await supabase
          .from('messages')
          .update({ read_at: null })
          .eq('id', msgs[0].id);

        if (updateError) throw updateError;
        await loadConversations();
      }
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  }, [loadConversations]);

  // ─── Filtered list ────────────────────────────────────────────
  const filteredConversations = useMemo(() => {
    return conversations.filter(c => {
      if (isHidden(c.entityType, c.id, c.lastMessageTime)) return false;

      const matchesSearch = c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone?.includes(searchQuery);

      let matchesFilter = true;
      if (filter === 'mine') {
        // "Minhas conversas" = qualquer conversa em que eu já enviei mensagem
        matchesFilter = c.hasMySentMessages;
      } else if (filter === 'unanswered') {
        matchesFilter = c.lastMessageDirection === 'inbound' && c.hasMySentMessages;
      }

      return matchesSearch && matchesFilter;
    });
  }, [conversations, searchQuery, filter, isHidden]);

  return {
    conversations: filteredConversations,
    allConversations: conversations,
    isLoading,
    searchQuery,
    setSearchQuery,
    filter,
    setFilter,
    unknownCount,
    markAsUnread,
    resolveBody,
    refetch: loadConversations,
    hideConversation: hide,
    unhideConversation: unhide,
  };
}
